import store from "../store";
import router from "../router";
import {
  normalizeAuthSessionMessage,
  sanitizeForDisplay,
  SESSION_EXPIRED_MESSAGE,
} from "./displayError";

const SESSION_KILL_CODES = new Set([
  "ACCOUNT_DISABLED",
  "ACCOUNT_REMOVED",
  "USER_NOT_FOUND",
]);

function isAuthLoginRequest(cfg) {
  if (!cfg) return false;
  const reqUrl = cfg.url || "";
  const base = cfg.baseURL || "";
  const combined = `${base}${reqUrl}`;
  return (
    reqUrl.includes("/api/auth/login") ||
    reqUrl.includes("/api/auth/select-agent-type") ||
    combined.includes("/api/auth/login") ||
    combined.includes("/api/auth/select-agent-type")
  );
}

function shouldForceLogout(error) {
  const status = error.response?.status;
  if (status === 401) return true;
  const code = error.response?.data?.code;
  if (status === 403 && code && SESSION_KILL_CODES.has(code)) return true;
  return false;
}

/**
 * Register Bearer token + 401/forced-session error handling on an axios instance.
 * Use on both global `axios` (raw calls) and `axios.create()` `api` client.
 */
export function attachAxiosAuth(client) {
  client.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem("token");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  client.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.data) {
        if (typeof error.response.data.error === "string") {
          error.response.data.error = sanitizeForDisplay(
            error.response.data.error
          );
        }
        if (typeof error.response.data.message === "string") {
          error.response.data.message = sanitizeForDisplay(
            error.response.data.message
          );
        }
        if (Array.isArray(error.response.data.userErrors)) {
          error.response.data.userErrors =
            error.response.data.userErrors.map((e) =>
              typeof e === "object" &&
              e !== null &&
              typeof e.message === "string"
                ? { ...e, message: sanitizeForDisplay(e.message) }
                : e
            );
        }
      }

      if (!shouldForceLogout(error)) {
        return Promise.reject(error);
      }

      const cfg = error.config || {};

      localStorage.removeItem("token");
      localStorage.removeItem("user");
      store.dispatch("logout");

      if (isAuthLoginRequest(cfg)) {
        return Promise.reject(error);
      }

      let msg =
        typeof error.response?.data?.error === "string"
          ? error.response.data.error.trim()
          : "";
      if (!msg && error.response?.status === 401) {
        msg = SESSION_EXPIRED_MESSAGE;
      } else if (msg) {
        msg = normalizeAuthSessionMessage(msg);
      }

      const onMainLoginRoute = router.currentRoute.value.path === "/login";

      if (onMainLoginRoute) {
        if (msg) {
          window.dispatchEvent(
            new CustomEvent("crm_auth_notice", { detail: msg })
          );
        }
        return Promise.reject(error);
      }

      if (msg) {
        try {
          sessionStorage.setItem("crm_auth_notice", msg);
        } catch (e) {
          /* ignore */
        }
      }
      window.location.href = "/login";
      return Promise.reject(error);
    }
  );
}
